import React, { useState, useEffect, useRef } from 'react';
import { SketchPicker } from 'react-color';
import StringProperty from './StringProperty';
import PropertyBase, { IBasePropertyProps } from './PropertyBase';
import DomUtils from '../../classes/utilities/DomUtils';
import { assert } from '../../classes/utilities/ErrorHandling';
import AnalyticsManager from '../../classes/AnalyticsManager';

interface IColorPropertyProps extends IBasePropertyProps
{
	hideField?: boolean;
	onBlur?: (e) => void;
	onFocus?: (e) => void;
}

export function CustomSketchPicker(props)
{
	const dataName = (props["data-name"] ? props["data-name"] + "-" : "") + "-color-picker";

	return (
		<SketchPicker
			disableAlpha={true}
			{...props}
			data-name={dataName + "-root"}
		/>
	);
}

/**
 * defines a color input using the browser's native color-picker dialog with an optional manual input field *internally uses a StringProperty*
 * @export
 * @param {{
 * value: String,
 * multivalues: Boolean,
 * onChangeCallback: (newValue: String),
 * onCommitCallback: (newValue: String),
 * "data-name": String
 * }} props this property expects and can use these special props:
 * - value: the current color value in hexadecimal string format, e.g. "#00bb3a". *if no '#' sign is present, it will be added automatically*
 * - multivalues: determines whether to draw the checkbox normally, or with a mark indicating the field is shared between many different values *optional*
 * - onChangeCallback: a callback function that will be executed whenever the value of either the input field or color picket changes, with the new value as the passed parameter *optional*
 * - onCommitCallback: a callback function that will be executed when the input field loses focus or the color picker popup is closed, with the new value as the passed parameter *optional*
 * - "data-name": used for automation purposes to specifically identify this property. *defaults to "-color-property". optional*
 * @returns a colored button that prompts the color picker to open, with an optional input field for manual hexadecimal color insertion
 */
export default function ColorProperty(props: IColorPropertyProps)
{
	const allowedKeys = "#abcdefABCDEF0123456789";

	const [colorPickerVisibility, setColorPickerVisibility] = useState(false);
	const colorPickerWrapper = useRef<HTMLDivElement>(null);
	const colorPickerParent = useRef<HTMLDivElement>(null);

	useEffect(() => 
	{
		return () => 
		{
			// there should only be one picker at any given time, and it's supposed to be ours
			document.getElementById("theColorPickerWrapper")?.remove();
		}
	}, []);

	function fnClose()
	{
		if (colorPickerWrapper.current && colorPickerWrapper.current instanceof HTMLElement)
		{
			//Hack: reparent this picker back to here...
			colorPickerParent.current!.appendChild(colorPickerWrapper.current);
			setColorPickerVisibility(false);
		}
	}

	function closeOnOutsideClick(e)
	{
		if(DomUtils.isDomElementDescendantOfRoot(e.target, colorPickerWrapper.current as HTMLElement))
		{
			return;
		}

		fnClose();
	}

	useEffect(() =>
	{
		if (colorPickerVisibility && colorPickerWrapper.current && colorPickerWrapper.current instanceof HTMLElement)
		{
			//Hack: reparent the picker to the App DOM element and reposition it properly
			const appElement = document.getElementById("App");
			assert(appElement, "App element not found! This should never happen!");
			if(!appElement) return; // TS doesn't know that assert will throw an error and stop execution

			appElement.appendChild(colorPickerWrapper.current);
			const rectParent = colorPickerParent.current!.getClientRects()[0];
			const rectPickerWrapper = colorPickerWrapper.current.getClientRects()[0];
			const rectApp = appElement.getClientRects()[0];

			const top = (rectParent.top + rectPickerWrapper.height + rectParent.height < rectApp.height) ? Math.floor(rectParent.top + rectParent.height) : Math.floor(rectApp.height) - Math.ceil(rectPickerWrapper.height);
			const left = (rectParent.left + rectPickerWrapper.width < rectApp.width) ? Math.floor(rectParent.left) : Math.floor(rectApp.width) - Math.ceil(rectPickerWrapper.width);

			colorPickerWrapper.current.style.left = left + "px";
			colorPickerWrapper.current.style.top = top + "px";
			colorPickerWrapper.current.style.zIndex = "1111111111111"; //This is needed to surface the picker above modal dialogs that use it...

			appElement.addEventListener('click', closeOnOutsideClick); //Also needed because of surfacing above modal

			return () =>
			{
				appElement.removeEventListener('click', closeOnOutsideClick);
				if (colorPickerWrapper.current && colorPickerWrapper.current instanceof HTMLElement)
				{
					colorPickerWrapper.current.remove();
				}
			}
		}
	}, [colorPickerVisibility]);


	let colorValue = props.value;
	if (colorValue && colorValue[0] !== "#")
	{
		colorValue = "#" + colorValue;
	}

	const dataName = (props["data-name"] ? props["data-name"] + "-" : "") + "-color-property";

	return (
		<PropertyBase {...props}
			data-name={dataName + "-root"}
			className={props.className}
		>

			<div
				ref={colorPickerParent}
				className={"TEMP_COLOR_PICKER_REPLACEMENT propertySmallCell"}
				style={{ backgroundColor: colorValue }}
				onClick={(e) =>
				{
					if(!props.disabled)	setColorPickerVisibility(true);
				}}
				//cellclass={'propertySmallCell'}
			>
				{
					!colorPickerVisibility ? null :
						<div
							ref={colorPickerWrapper}
							id={"theColorPickerWrapper"}
							className="TEMP_COLOR_PICKER"
							style={{
								position: "absolute",
								zIndex: 2000,
							}}
						>
							<CustomSketchPicker
								color={colorValue}
								onChange={(color) =>
								{
									if (props.onChangeCallback)
									{
										props.onChangeCallback(color.hex);
									}
								}
								}
								onChangeComplete={(color) =>
								{
									if (props.onCommitCallback)
									{
										AnalyticsManager.managedEvent('propertyChanged', 'propertyId', dataName + "-picker");
										props.onCommitCallback(color.hex);
									}
								}
								}
								fnClose={fnClose}
							/>
						</div>
				}
			</div>
			{
				props.hideField ? null :
					<StringProperty
						className={"colorPropertyHexField"}
						data-name={dataName + "-text-input"}
						value={colorValue}
						disabled={props.disabled}
						allowedKeys={allowedKeys}
						multivalues={props.multivalues}
						onChangeCallback={props.onChangeCallback}
						onCommitCallback={props.onCommitCallback}
						onBlur={props.onBlur}
						onFocus={props.onFocus}
						contextMenuOverride={props.contextMenuOverride}
					/>
			}
		</PropertyBase>
	);
}
